import { observer } from 'mobx-react'
import { FC, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import styles from './index.module.scss'
import { IJobBase, MessageType } from '../../../types'
import { PrimaryHeading } from '../../atoms/PrimaryHeading'
import { getJobThumbnail, getSignInPath, useStores } from '../../../utils'
import { Button } from '../../atoms/Button'
import { SaveJobButtonIcon } from '../SaveJobButtonIcon'
import { JobItemContentLabel } from '../JobItemContentLabel'
import { JobAttributes } from '../JobAttributes'

type Props = {
  job: IJobBase
}

export const JobHero: FC<Props> = observer(({ job }) => {
  const { t } = useTranslation(['jobs-[job_slug]', 'jobs'])
  const { messages, viewer, ui } = useStores()
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const openJobApplicationModal = () => {
    if (viewer.isSignedIn) {
      ui.toggleJobApplicationModal()
    } else {
      const path = getSignInPath(router.asPath)
      router.push(path)
    }
  }

  const isCompanyMember = viewer.viewer?.isCompanyMember(job?.company.slug)

  const addMyJobBookmark = async () => {
    if (!viewer.isSignedIn) {
      return
    }
    setIsSubmitting(true)
    const isSuccessful = await viewer.viewer?.addMyJobBookmark(job?.id)
    if (isSuccessful) {
      messages.add({
        type: MessageType.Info,
        body: t('jobs:募集を保存しました'),
        isTranslated: true,
        ttl: 5000,
      })
    }
    setIsSubmitting(false)
  }

  const removeMyJobBookmark = async () => {
    if (!viewer.isSignedIn) {
      return
    }
    setIsSubmitting(true)
    const isSuccessful = await viewer.viewer?.removeMyJobBookmark(job.id)
    if (isSuccessful) {
      messages.add({
        type: MessageType.Info,
        body: t('jobs:保存を解除しました'),
        isTranslated: true,
        ttl: 5000,
      })
    }
    setIsSubmitting(false)
  }

  const isJobSaved = viewer.viewer.isInMyJobBookmarks(job.id)
  const onClickSaveButton = () => {
    if (isJobSaved) {
      removeMyJobBookmark()
    } else {
      addMyJobBookmark()
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <PrimaryHeading backgroundText='JOB' htmlTagType='h2'>
          {t('求人概要')}
        </PrimaryHeading>
      </div>
      <div className={styles.label}>
        <JobItemContentLabel job={job} size='large' />
      </div>
      <div className={styles.thumb}>
        <img src={getJobThumbnail(job)} alt={job?.title} className='img-fluid' />
      </div>
      <h3 className={styles.title}>{job?.title}</h3>
      <div className={styles.footer}>
        <div className={styles.meta}>
          <JobAttributes job={job} />
        </div>
        {viewer.isSignedIn && (
          <div className={styles.buttonContainer}>
            <div className={styles.save}>
              <SaveJobButtonIcon
                disabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={onClickSaveButton}
                isSaved={isJobSaved}
              />
            </div>
            <div className={styles.button}>
              <Button
                styleType='blue'
                componentType='button'
                buttonType='button'
                buttonSize='medium'
                disabled={isCompanyMember}
                onClick={openJobApplicationModal}
              >
                {viewer.isSignedIn ? t('応募する') : t('ログインして応募')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})
